import {
  normalizeTimeSlotLabel,
  SLOT_CATALOG,
  DAYS_OF_WEEK,
  TIME_SLOTS,
  DAY_ID_TO_LABEL,
  DAY_LABEL_TO_ID,
  API_DAY_NAME_TO_LABEL,
} from '../utils/sessionUtils';
import * as mentorRequestLog from './mentorRequestLog';

const IS_LOCAL_ENV = process.env.REACT_APP_ENV === 'local';
const LOCAL_CENTRAL_BASE_URL = process.env.REACT_APP_CENTRAL_BASE_URL; // e.g. http://127.0.0.1:8000
const LOCAL_MENTOR_BASE_URL = process.env.REACT_APP_API_BASE_URL; // e.g. http://127.0.0.1:8002

const AUTH_BASE_URL = (IS_LOCAL_ENV && LOCAL_CENTRAL_BASE_URL)
  ? `${LOCAL_CENTRAL_BASE_URL}/api-mentor/v1`
  : 'https://api.ifspd.eu/api-mentor/v1';
const MENTOR_BASE_URL = (IS_LOCAL_ENV && LOCAL_MENTOR_BASE_URL)
  ? `${LOCAL_MENTOR_BASE_URL}/api/v1`
  : 'https://mentor-api.portahead.com/api/v1';
const SSO_BASE_URL = (IS_LOCAL_ENV && LOCAL_CENTRAL_BASE_URL)
  ? `${LOCAL_CENTRAL_BASE_URL}/api-sso/v1`
  : 'https://api.ifspd.eu/api-sso/v1';
const TOKEN_KEY = 'mentorToken';
const USER_KEY = 'mentorUser';

// Live API level_name -> internal convention used by cohort levelYear.
const API_LEVEL_TO_INTERNAL = { Basic: 'Year 1', Intermediate: 'Year 2' };

function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

const inFlightGetRequests = new Map();
const year2SetupCohortsCache = new Map();
const year2SetupCache = new Map();
const year2CouncilNominationsCache = new Map();

// Result caches for workspace boot reads — invalidated on matching mutations
const cohortsCache = new Map();
const sessionsCache = new Map();
const evaluationsCache = new Map();
const groupsCache = new Map();
const year2SetupSchoolsCache = new Map();
const year2SetupCohortsBySchoolCache = new Map();

function cacheKey(baseUrl, path, token) {
  return `${baseUrl}::${path}::${token || ''}`;
}

function parseHost(baseUrl) {
  try {
    return new URL(baseUrl).host;
  } catch {
    return String(baseUrl || 'unknown');
  }
}

function readCache(cache, key, baseUrl, path, method = 'GET') {
  if (!cache.has(key)) return null;
  mentorRequestLog.recordCacheHit({ baseUrl, path, method });
  return cache.get(key);
}

export function clearReadCaches() {
  inFlightGetRequests.clear();
  year2SetupCohortsCache.clear();
  year2SetupCache.clear();
  year2CouncilNominationsCache.clear();
  cohortsCache.clear();
  sessionsCache.clear();
  evaluationsCache.clear();
  groupsCache.clear();
  year2SetupSchoolsCache.clear();
  year2SetupCohortsBySchoolCache.clear();
}

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

async function requestWithBase(baseUrl, path, options = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  const isRetryableMethod = method === 'GET';
  const token = getToken();
  const { callerTag, ...fetchOptions } = options;
  const host = parseHost(baseUrl);
  const requestKey = method === 'GET' ? cacheKey(baseUrl, path, token) : null;
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

  if (requestKey && inFlightGetRequests.has(requestKey)) {
    mentorRequestLog.recordDeduped({ baseUrl, path, method, callerTag });
    return inFlightGetRequests.get(requestKey);
  }

  const attemptRequest = async () => {
    let res;
    try {
      res = await fetch(`${baseUrl}${path}`, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(fetchOptions.headers || {}),
        },
      });
    } catch (networkErr) {
      const err = new Error(networkErr?.message || 'Network error');
      err.baseUrl = baseUrl;
      err.endpoint = path;
      throw err;
    }
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = { status: res.status, data: null, message: res.statusText || 'API error' };
    }

    if (res.ok || data?.status === 200 || data?.status === 201) {
      const responseBytes = JSON.stringify(data).length;
      return { payload: data, status: res.status || data?.status || 200, responseBytes };
    }

    const err = new Error(data?.message || res.statusText || 'API error');
    err.status = res.status || data?.status;
    err.data = data;
    err.baseUrl = baseUrl;
    err.endpoint = path;
    throw err;
  };

  const shouldRetry = (err) => {
    if (!isRetryableMethod) return false;
    if (!err) return false;
    if (!err.response && !err.status) return true;
    return [502, 503, 504].includes(Number(err.status));
  };

  const requestPromise = (async () => {
    let retried = false;
    try {
      let result = await attemptRequest();
      const durationMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt);
      mentorRequestLog.record({
        host,
        method,
        path,
        status: result.status,
        durationMs,
        callerTag,
        retried,
        outcome: 'network',
        responseBytes: result.responseBytes,
      });
      return result.payload;
    } catch (err) {
      if (shouldRetry(err)) {
        retried = true;
        console.warn('[mentor-boot] retrying request after cold-start failure:', `${baseUrl}${path}`);
        await new Promise(resolve => setTimeout(resolve, 800));
        try {
          const result = await attemptRequest();
          const durationMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt);
          mentorRequestLog.record({
            host,
            method,
            path,
            status: result.status,
            durationMs,
            callerTag,
            retried: true,
            outcome: 'network',
            responseBytes: result.responseBytes,
          });
          return result.payload;
        } catch (retryErr) {
          const durationMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt);
          mentorRequestLog.record({
            host,
            method,
            path,
            status: retryErr.status || null,
            durationMs,
            callerTag,
            retried: true,
            outcome: 'network',
            errorMessage: retryErr.message,
          });
          throw retryErr;
        }
      }
      const durationMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt);
      mentorRequestLog.record({
        host,
        method,
        path,
        status: err.status || null,
        durationMs,
        callerTag,
        retried,
        outcome: 'network',
        errorMessage: err.message,
      });
      throw err;
    }
  })();

  if (requestKey) {
    inFlightGetRequests.set(requestKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      if (inFlightGetRequests.get(requestKey) === requestPromise) {
        inFlightGetRequests.delete(requestKey);
      }
    }
  }

  return requestPromise;
}

function request(path, options = {}) {
  return requestWithBase(AUTH_BASE_URL, path, options);
}

function requestMentor(path, options = {}) {
  return requestWithBase(MENTOR_BASE_URL, path, options);
}

function requestSso(path, options = {}) {
  return requestWithBase(SSO_BASE_URL, path, options);
}

function unwrapData(payload) {
  if (payload == null) return null;
  if (Array.isArray(payload)) return payload;
  if (Object.prototype.hasOwnProperty.call(payload, 'data')) return payload.data;
  return payload;
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function toId(value) {
  return value == null ? '' : String(value);
}

function uniqueAuditorAssignments(assignments) {
  const statusRank = { accepted: 0, pending: 1, rejected: 2, withdrawn: 3 };
  const byAuditor = new Map();

  for (const assignment of asArray(assignments)) {
    const auditorId = toId(assignment.user_id ?? assignment.auditorId);
    if (!auditorId) continue;

    const normalized = {
      id: toId(assignment.id),
      auditorId,
      auditorName: assignment.name || '',
      roomNumber: assignment.breakout_room_number ?? assignment.breakoutRoomNumber ?? null,
      assignmentStatus: assignment.assignment_status ?? assignment.assignmentStatus ?? 'pending',
      responseAt: assignment.response_at ?? assignment.responseAt ?? null,
    };

    const existing = byAuditor.get(auditorId);
    if (!existing) {
      byAuditor.set(auditorId, normalized);
      continue;
    }

    const existingRank = statusRank[String(existing.assignmentStatus).toLowerCase()] ?? 99;
    const newRank = statusRank[String(normalized.assignmentStatus).toLowerCase()] ?? 99;
    if (newRank < existingRank) {
      byAuditor.set(auditorId, normalized);
    }
  }

  return Array.from(byAuditor.values());
}

function uniqueAuditorIds(assignments) {
  return uniqueAuditorAssignments(assignments).map(a => a.auditorId);
}

function normalizeSchoolName(item) {
  return item?.school_name || item?.schoolName || item?.school?.name || item?.school?.school_name || '';
}

function titleCase(value) {
  if (!value) return '';
  return String(value)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function normalizeCohortAssignments(items) {
  return asArray(items).map(a => ({
    auditorId: toId(a.user_id ?? a.auditor_id ?? a.id),
    isStandby: Boolean(a.is_standby ?? a.isStandby),
    breakoutRoomNumber: a.breakout_room_number ?? a.breakoutRoomNumber ?? null,
    assignmentStatus: a.assignment_status ?? a.assignmentStatus ?? 'pending',
    responseAt: a.response_at ?? a.responseAt ?? null,
    id: a.id ?? null,
    name: a.name || '',
  }));
}

export function normalizeCohort(item) {
  if (!item) return null;
  const students = asArray(item.students);
  const rawStatus = item.status || 'active';
  return {
    id: toId(item.id),
    name: item.name || '',
    schoolId: toId(item.school_id ?? item.schoolId),
    schoolName: normalizeSchoolName(item),
    branchId: toId(item.branch_id ?? item.branchId),
    branchName: item.branch_name || item.branchName || '',
    spocId: toId(item.spoc_id ?? item.spocId),
    spocName: item.spoc_name || item.spocName || null,
    levelYear: item.level_year ?? item.levelYear ?? null,
    status: titleCase(rawStatus) || 'Active',
    studentIds: students.length > 0
      ? students.map(student => toId(student.id ?? student.student_id ?? student.studentId))
      : asArray(item.student_ids ?? item.studentIds).map(toId),
    studentCount: item.student_count ?? item.studentCount ?? students.length ?? 0,
    sessionCount: item.session_count ?? item.sessionCount ?? 0,
    counselorIds: asArray(item.counselor_ids ?? item.counselorIds).map(toId),
    auditorAssignments: normalizeCohortAssignments(item.auditor_assignments ?? item.auditorAssignments),
    createdAt: item.created_at ?? item.createdAt ?? '',
  };
}

export function normalizeCohortDetail(item) {
  if (!item) return null;
  const base = normalizeCohort(item);
  return {
    ...base,
    studentIds: asArray(item.students).length > 0
      ? asArray(item.students).map(student => toId(student.id))
      : base.studentIds,
    students: asArray(item.students).map(student => ({
      id: toId(student.id),
      name: student.name || '',
      grade: student.grade || '',
    })),
    counselorIds: asArray(item.counselor_ids).map(toId),
    auditorAssignments: normalizeCohortAssignments(item.auditor_assignments),
    sessions: asArray(item.sessions).map(session => ({
      id: toId(session.id),
      sessionType: session.session_type || '',
      scheduledAt: session.scheduled_at || '',
      status: session.status || '',
    })),
  };
}

export function normalizeGroup(item) {
  if (!item) return null;
  return {
    id: toId(item.id),
    name: item.name || '',
    schoolId: toId(item.school_id ?? item.schoolId),
    schoolName: item.school_name || item.schoolName || '',
    cohortIds: asArray(item.cohort_ids ?? item.cohortIds).map(toId),
    cohorts: asArray(item.cohorts).map(c => ({
      id: toId(c.id),
      name: c.name || '',
      levelYear: c.level_year ?? c.levelYear ?? null,
      status: c.status || 'active',
    })),
    createdAt: item.created_at ?? item.createdAt ?? '',
  };
}

export function normalizeSession(item) {
  if (!item) return null;
  const rawType = item.session_type || item.sessionType || '';
  const councilNumber = item.council_number ?? item.councilNumber ?? null;
  const rawStatus = item.status || 'planned';
  const displayType = rawType.toLowerCase() === 'orientation'
    ? 'Orientation'
    : rawType.toLowerCase() === 'council'
      ? `Council ${councilNumber ?? ''}`.trim()
      : titleCase(rawType);
  return {
    id: toId(item.id),
    cohortId: toId(item.cohort_id ?? item.cohortId),
    cohortName: item.cohort_name || item.cohortName || '',
    groupId: toId(item.group_id ?? item.groupId),
    groupName: item.group_name || item.groupName || '',
    groupCohortIds: asArray(item.group_cohort_ids ?? item.groupCohortIds).map(toId),
    schoolId: toId(item.school_id ?? item.schoolId),
    branchId: toId(item.branch_id ?? item.branchId),
    schoolName: item.school_name || item.schoolName || '',
    levelYear: item.cohort_level_year ?? item.group_level_year ?? item.levelYear ?? null,
    sessionNumber: Number(item.session_number ?? item.sessionNumber ?? 0),
    sessionType: displayType,
    councilNumber,
    scheduledAt: item.scheduled_at || item.scheduledAt || '',
    slotStartTime: item.slot_start_time || item.slotStartTime || '',
    slotEndTime: item.slot_end_time || item.slotEndTime || '',
    timeSlot: normalizeTimeSlotLabel(
      item.time_slot || item.timeSlot || '',
      item.slot_start_time || item.slotStartTime || ''
    ),
    meetingLink: item.meeting_link || item.meetingLink || '',
    comments: item.comments || '',
    status: titleCase(rawStatus) || 'Planned',
    counselorId: toId(item.counselor_id ?? item.counselorId),
    counselorName: item.counselor_name || item.counselorName || '',
    spocId: toId(item.spoc_id ?? item.spocId),
    spocName: item.spoc_name || item.spocName || '',
    resources: asArray(item.resource_ids ?? item.resourceIds).map(toId),
    auditorAssignments: uniqueAuditorAssignments(item.auditor_assignments ?? item.auditorAssignments),
    auditors: uniqueAuditorIds(item.auditor_assignments ?? item.auditorAssignments),
    cohortStudentIds: asArray(item.cohort_student_ids ?? item.cohortStudentIds).map(toId),
    cohortStudentIdsByCohort: normalizeCohortStudentIdsByCohort(
      item.cohort_student_ids_by_cohort ?? item.cohortStudentIdsByCohort
    ),
    nominationsByCohort: normalizeRecordByCohort(
      item.nominations_by_cohort ?? item.nominationsByCohort,
      normalizeNomination
    ),
    outcomesByCohort: normalizeRecordByCohort(
      item.outcomes_by_cohort ?? item.outcomesByCohort,
      normalizeOutcome
    ),
    nomination: item.nomination ? normalizeNomination(item.nomination) : null,
    outcome: item.outcome ? normalizeOutcome(item.outcome) : null,
    nextSessionOpened: item.next_session_opened || item.nextSessionOpened || null,
  };
}

function normalizeRecordByCohort(raw, normalizer) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.entries(raw).forEach(([cohortId, value]) => {
    out[toId(cohortId)] = value ? normalizer(value) : null;
  });
  return out;
}

function normalizeCohortStudentIdsByCohort(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.entries(raw).forEach(([cohortId, ids]) => {
    out[toId(cohortId)] = asArray(ids).map(toId);
  });
  return out;
}

function normalizedIdList(primary, fallbackIds) {
  const fromPrimary = asArray(primary).map(toId).filter(Boolean);
  if (fromPrimary.length > 0) return fromPrimary;
  return asArray(fallbackIds).map(toId).filter(Boolean);
}

function normalizedNameList(primary, fallbackNames) {
  const fromPrimary = asArray(primary).filter(Boolean);
  if (fromPrimary.length > 0) return fromPrimary;
  return asArray(fallbackNames).filter(Boolean);
}

export function normalizeNomination(item) {
  if (!item) return null;
  return {
    sessionId: toId(item.session_id ?? item.sessionId),
    cohortId: toId(item.cohort_id ?? item.cohortId),
    chairId: toId(item.chair_id ?? item.chairId),
    chairName: item.chair_name ?? item.chairName ?? '',
    presenterIds: normalizedIdList(item.presenterIds, [
      item.presenter1_id,
      item.presenter2_id,
      item.presenter3_id,
      item.presenter4_id,
    ]),
    presenterNames: normalizedNameList(item.presenterNames, [
      item.presenter1_name ?? item.presenter1Name,
      item.presenter2_name ?? item.presenter2Name,
      item.presenter3_name ?? item.presenter3Name,
      item.presenter4_name ?? item.presenter4Name,
    ]),
    mentorIds: normalizedIdList(item.mentorIds, [
      item.mentor1_id,
      item.mentor2_id,
      item.mentor3_id,
      item.mentor4_id,
    ]),
    mentorNames: normalizedNameList(item.mentorNames, [
      item.mentor1_name ?? item.mentor1Name,
      item.mentor2_name ?? item.mentor2Name,
      item.mentor3_name ?? item.mentor3Name,
      item.mentor4_name ?? item.mentor4Name,
    ]),
    nominatedBy: toId(item.nominated_by ?? item.nominatedBy),
    nominatedAt: item.nominated_at ?? item.nominatedAt ?? '',
  };
}

export function normalizeOutcome(item) {
  if (!item) return null;
  return {
    sessionId: toId(item.session_id ?? item.sessionId),
    cohortId: toId(item.cohort_id ?? item.cohortId),
    challengerIds: normalizedIdList(item.challengerIds, [
      item.challenger1_id,
      item.challenger2_id,
      item.challenger3_id,
      item.challenger4_id,
    ]),
    challengerNames: normalizedNameList(item.challengerNames, [
      item.challenger1_name ?? item.challenger1Name,
      item.challenger2_name ?? item.challenger2Name,
      item.challenger3_name ?? item.challenger3Name,
      item.challenger4_name ?? item.challenger4Name,
    ]),
    bestPresenterId: toId(item.best_presenter_id ?? item.bestPresenterId),
    bestPresenterName: item.best_presenter_name ?? item.bestPresenterName ?? '',
    bestMentorId: toId(item.best_mentor_id ?? item.bestMentorId),
    bestMentorName: item.best_mentor_name ?? item.bestMentorName ?? '',
    bestChallengerId: toId(item.best_challenger_id ?? item.bestChallengerId),
    bestChallengerName: item.best_challenger_name ?? item.bestChallengerName ?? '',
    chairScore: item.chair_score ?? item.chairScore ?? '',
    loggedBy: toId(item.logged_by ?? item.loggedBy),
    loggedAt: item.logged_at ?? item.loggedAt ?? '',
  };
}

export function normalizeEvaluation(item) {
  if (!item) return null;
  const rawStatus = item.status || 'draft';
  const status = rawStatus === 'submitted' ? 'pending' : rawStatus;
  return {
    id: toId(item.id),
    sessionAuditorId: toId(item.session_auditor_id ?? item.sessionAuditorId),
    sessionId: toId(item.session_id ?? item.sessionId),
    auditorName: item.auditor_name || item.auditorName || '',
    studentId: toId(item.student_id ?? item.studentId),
    studentName: item.student_name || item.studentName || '',
    role: item.role || '',
    positiveKeywords: asArray(item.positive_keywords ?? item.positiveKeywords),
    negativeKeywords: asArray(item.negative_keywords ?? item.negativeKeywords),
    rating: item.rating ?? null,
    comment: item.comment || '',
    status,
    submittedAt: item.submitted_at ?? item.submittedAt ?? '',
    approvedBy: toId(item.approved_by ?? item.approvedBy),
    approvedAt: item.approved_at ?? item.approvedAt ?? '',
    rejectionReason: item.rejection_reason ?? item.rejectionReason ?? '',
    isRevision: item.is_revision ?? item.isRevision ?? false,
    revisionNumber: item.revision_number ?? item.revisionNumber ?? 1,
  };
}

export function normalizeEvaluationLog(item) {
  if (!item) return null;
  const base = normalizeEvaluation({
    ...item,
    status: 'approved',
    submitted_at: item.approved_at ?? item.approvedAt ?? '',
  });
  return {
    ...base,
    evaluationId: toId(item.evaluation_id ?? item.evaluationId),
    approvedByName: item.approved_by_name ?? item.approvedByName ?? '',
    originalApprovedAt: item.original_approved_at ?? item.originalApprovedAt ?? '',
    cohortId: toId(item.cohort_id ?? item.cohortId),
    cohortName: item.cohort_name ?? item.cohortName ?? '',
    sessionType: item.session_type ?? item.sessionType ?? '',
    sessionDate: item.session_date ?? item.sessionDate ?? '',
    timeSlot: item.time_slot ?? item.timeSlot ?? '',
    schoolName: item.school_name ?? item.schoolName ?? '',
    source: 'log',
  };
}

function buildQuery(params = {}) {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '');
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries.map(([key, value]) => [key, String(value)])).toString();
}

// Persists a mentor JWT (and the user derived from its claims) exactly the
// way a normal login does, whether the token came from a direct credential
// login or was redeemed via the SSO handoff from portahead.
export function persistMentorSession(token, extraUserFields = {}) {
  sessionStorage.setItem(TOKEN_KEY, token);
  const claims = decodeJwt(token);
  const user = { ...extraUserFields, ...claims };
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

export async function login(email, password) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    callerTag: 'login:submit',
  });
  return persistMentorSession(data.token, data.data);
}

// Redeems a single-use SSO handoff code (minted by portahead after a
// successful mentor login there) for the mentor JWT it carries, and persists
// the session exactly like a direct login (first_name/last_name included).
export async function redeemSsoCode(code) {
  const data = await requestSso('/mentor/redeem-code', {
    method: 'POST',
    body: JSON.stringify({ code }),
    callerTag: 'sso:redeem',
  });
  return persistMentorSession(data.data.token, data.data.data);
}

export async function logout() {
  await mentorRequestLog.shutdown();
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem('mentor_api_log_session');
  clearReadCaches();
  mentorRequestLog.initSession(true);
}

export function getStoredUser() {
  try { return JSON.parse(sessionStorage.getItem(USER_KEY)); } catch { return null; }
}

export function isTokenValid() {
  const token = getToken();
  if (!token) return false;
  const claims = decodeJwt(token);
  if (!claims) return false;
  return claims.exp > Math.floor(Date.now() / 1000);
}

export async function getBranches(schoolId) {
  const path = schoolId ? `/branches?school_id=${schoolId}` : '/branches';
  const data = await request(path);
  return data.data;
}

export async function getSchools(branchId) {
  const path = branchId ? `/schools?branch_id=${branchId}` : '/schools';
  const data = await request(path);
  return data.data;
}

export async function getStudents(schoolId, branchId) {
  const path = branchId
    ? `/students?school_id=${schoolId}&branch_id=${branchId}`
    : `/students?school_id=${schoolId}`;
  const data = await requestMentor(path);
  return data.data;
}

export async function getStudentSyncStatus(schoolId, branchId) {
  const path = branchId
    ? `/students/sync-status?school_id=${schoolId}&branch_id=${branchId}`
    : `/students/sync-status?school_id=${schoolId}`;
  const data = await requestMentor(path);
  return data.data;
}

export async function syncStudents(schoolId, branchId) {
  const data = await requestMentor('/students/sync', {
    method: 'POST',
    body: JSON.stringify({ school_id: schoolId, branch_id: branchId || null }),
  });
  return data.data;
}

export async function getTeachers(schoolId, branchId) {
  const data = await request(`/teachers?school_id=${schoolId}&branch_id=${branchId}`);
  return data.data;
}

// Normalize live teacher API shape → internal { id, name, email, schoolId, branchId }
export function normalizeTeachers(items, schoolId, branchId) {
  return items.map(t => ({
    id: String(t.id || t.external_id),
    name: t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || String(t.id),
    email: t.user_email?.email || t.email || '',
    schoolId: String(schoolId),
    branchId: branchId ? String(branchId) : '',
  }));
}

export async function getCounselors(schoolId = null, branchId = null) {
  const params = [];
  if (schoolId) params.push(`school_id=${schoolId}`);
  if (branchId) params.push(`branch_id=${branchId}`);
  const qs = params.length ? '?' + params.join('&') : '';
  const data = await request(`/counselors${qs}`);
  return data.data;
}

export async function getAuditors(schoolId = null, branchId = null) {
  const params = [];
  if (schoolId) params.push(`school_id=${schoolId}`);
  if (branchId) params.push(`branch_id=${branchId}`);
  const qs = params.length ? '?' + params.join('&') : '';
  const data = await request(`/auditors${qs}`);
  return data.data;
}

// Normalize live auditor API shape → internal { id, name, email, availabilitySlots }
// API slot label "7-9 AM" (ASCII hyphen) → internal "7–9 AM" (en-dash) to match TIME_SLOTS
const API_SLOT_LABEL_MAP = {
  '7-9 AM':       '7–9 AM',
  '9-11 AM':      '9–11 AM',
  '11 AM-1 PM':   '11 AM–1 PM',
  '11 AM - 1 PM': '11 AM–1 PM',
  '11AM-1PM':     '11 AM–1 PM',
  '1-3 PM':       '1–3 PM',
  '1 - 3 PM':     '1–3 PM',
  '3-5 PM':       '3–5 PM',
  '3 - 5 PM':     '3–5 PM',
};

export function normalizeAuditors(items) {
  return (items || []).map(a => {
    // The live API groups slots under each weekday, while older responses
    // returned flat slot rows. Keep one internal flat contract for both.
    const availabilitySlots = (a.availability_slots || []).flatMap(day => {
      if (Array.isArray(day?.slots)) {
        return day.slots.map(slot => ({ ...slot, day: day.day, dayId: day.day_id }));
      }
      return [day];
    }).map(s => ({
      id: s.id ?? s.slot_id,
      day: s.day || '',
      dayId: s.day_id ?? null,
      slot: API_SLOT_LABEL_MAP[s.slot] || s.slot || '',
      startTime: s.start_time || s.startTime || '',
      endTime: s.end_time || s.endTime || '',
    })).filter(s => s.slot || s.startTime);

    return {
      id: String(a.auditor_id || a.id),
      name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.name || String(a.auditor_id || a.id),
      email: a.email || '',
      college: a.college || '',
      collegeYear: a.college_year || '',
      subjectSpecialisation: a.subject_specialisation || '',
      internshipStartDate: a.internship_start_date || '',
      availabilitySlots,
    };
  });
}

// Normalize live counselor API shape → internal { id, name, email }
// Counselor admin form captures: first_name, last_name, role, email — no counselor_id, just id.
// School/branch fields removed from response by Ritika (2026-06-10).
export function normalizeCounselors(items) {
  return (items || []).map(c => ({
    id: String(c.id),
    name: c.full_name?.trim() || c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || String(c.id),
    email: c.user_email?.email || c.email || '',
  }));
}

const SLOT_TO_TIMES = Object.fromEntries(
  SLOT_CATALOG.map(s => [s.label, { start_time: s.start, end_time: s.end }])
);

const START_TO_LABEL = Object.fromEntries(
  SLOT_CATALOG.map(s => [s.start, s.label])
);

/**
 * Build empty day×slot availability grid used by the auditor UI.
 * Shape: { Mon: { '7–9 AM': false, ... }, ... }
 */
export function emptyAvailabilityGrid() {
  const grid = {};
  DAYS_OF_WEEK.forEach(day => {
    grid[day] = {};
    TIME_SLOTS.forEach(slot => {
      grid[day][slot] = false;
    });
  });
  return grid;
}

function resolveDayLabel(dayId, dayName) {
  if (dayId != null && DAY_ID_TO_LABEL[Number(dayId)]) {
    return DAY_ID_TO_LABEL[Number(dayId)];
  }
  if (dayName && API_DAY_NAME_TO_LABEL[dayName]) {
    return API_DAY_NAME_TO_LABEL[dayName];
  }
  if (dayName && DAYS_OF_WEEK.includes(dayName)) return dayName;
  return null;
}

function resolveSlotLabel(slotRow) {
  if (!slotRow) return null;
  const fromStart = START_TO_LABEL[slotRow.start_time || slotRow.startTime || ''];
  if (fromStart) return fromStart;
  const raw = slotRow.slot || slotRow.label || '';
  return API_SLOT_LABEL_MAP[raw] || (TIME_SLOTS.includes(raw) ? raw : null) || normalizeTimeSlotLabel(raw) || null;
}

/**
 * Normalize GET /auditor/availability (or auditors[].availability_slots) into a day×slot grid.
 *
 * Accepts:
 * - day-grouped: [{ day_id, day, slots: [{ start_time, end_time, slot }] }]
 * - flat legacy rows: [{ day_id, day, slot, start_time }]
 * - empty / null
 */
export function normalizeAuditorAvailability(raw) {
  const grid = emptyAvailabilityGrid();
  const rows = Array.isArray(raw) ? raw : [];

  rows.forEach(row => {
    if (!row) return;

    // Day-grouped shape (Postman + GET /auditors)
    if (Array.isArray(row.slots)) {
      const dayLabel = resolveDayLabel(row.day_id ?? row.dayId, row.day);
      if (!dayLabel || !grid[dayLabel]) return;
      row.slots.forEach(slotRow => {
        const slotLabel = resolveSlotLabel(slotRow);
        if (slotLabel && Object.prototype.hasOwnProperty.call(grid[dayLabel], slotLabel)) {
          grid[dayLabel][slotLabel] = true;
        }
      });
      return;
    }

    // Flat row shape
    const dayLabel = resolveDayLabel(row.day_id ?? row.dayId, row.day);
    const slotLabel = resolveSlotLabel(row);
    if (dayLabel && slotLabel && grid[dayLabel] && Object.prototype.hasOwnProperty.call(grid[dayLabel], slotLabel)) {
      grid[dayLabel][slotLabel] = true;
    }
  });

  return grid;
}

/**
 * Build Ritika POST body for day-based availability:
 * { availability: [{ day_id, slots: [{ start_time, end_time }] }] }
 */
export function buildAuditorAvailabilityPayload(grid) {
  const availability = [];
  DAYS_OF_WEEK.forEach(day => {
    const dayId = DAY_LABEL_TO_ID[day];
    if (!dayId) return;
    const slots = TIME_SLOTS
      .filter(slot => grid?.[day]?.[slot])
      .map(slot => SLOT_TO_TIMES[slot])
      .filter(Boolean);
    if (slots.length > 0) {
      availability.push({ day_id: dayId, slots });
    }
  });
  return { availability };
}

function gridHasAnySlot(grid) {
  return DAYS_OF_WEEK.some(day => TIME_SLOTS.some(slot => grid?.[day]?.[slot]));
}

/**
 * Load the logged-in auditor's weekly availability as a day×slot grid.
 *
 * Primary: GET /auditor/availability
 * Fallback: GET /auditors and match self by JWT user_id (Ritika's self-GET
 * currently returns [] even after a successful day-based save — data is
 * visible on the auditors directory payload).
 */
export async function getAuditorAvailability() {
  const data = await request('/auditor/availability');
  let grid = normalizeAuditorAvailability(data?.data);

  if (!gridHasAnySlot(grid)) {
    try {
      const claims = decodeJwt(getToken() || '') || {};
      const selfId = String(claims.user_id || claims.sub || claims.id || '');
      if (selfId) {
        const auditors = await getAuditors();
        const self = (auditors || []).find(a =>
          String(a.auditor_id || a.user_id || a.id) === selfId
        );
        if (self?.availability_slots) {
          grid = normalizeAuditorAvailability(self.availability_slots);
        }
      }
    } catch (err) {
      // Directory fallback is best-effort; keep empty grid from primary GET.
      console.warn('Auditor availability directory fallback failed:', err);
    }
  }

  return grid;
}

/**
 * Persist day×slot grid via POST /auditor/availability (Ritika day_id contract).
 * @param {object} grid - { Mon: { '7–9 AM': true, ... }, ... }
 */
export async function saveAuditorAvailability(grid) {
  const payload = buildAuditorAvailabilityPayload(grid);
  const data = await request('/auditor/availability', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return data;
}

export async function getResources(page = 1, limit = 30) {
  const data = await request(`/resources?page=${page}&limit=${limit}`);
  return { items: data.data, total: data.total };
}

// Keep resource boot on one request; the live API returns all records with
// limit=100 and the second page used to fail during cold starts.
export async function getAllResources(limit = 100) {
  const items = [];
  let page = 1;
  let total = 0;

  do {
    let result;
    try {
      result = await getResources(page, limit);
    } catch (err) {
      // Keep already-loaded resources when a later pagination request fails.
      if (items.length > 0) break;
      throw err;
    }
    const pageItems = asArray(result.items);
    items.push(...pageItems);
    total = Number(result.total) || items.length;
    if (pageItems.length === 0) break;
    page += 1;
  } while (items.length < total);

  return items;
}

export async function getCohorts() {
  const token = getToken();
  const key = cacheKey(MENTOR_BASE_URL, '/cohorts', token);
  const cachedCohorts = readCache(cohortsCache, key, MENTOR_BASE_URL, '/cohorts');
  if (cachedCohorts != null) return cachedCohorts;
  const promise = requestMentor('/cohorts').then(d => asArray(unwrapData(d)).map(normalizeCohort));
  cohortsCache.set(key, promise);
  try {
    const result = await promise;
    cohortsCache.set(key, result);
    return result;
  } catch (err) {
    cohortsCache.delete(key);
    throw err;
  }
}

export async function getCohort(id) {
  const data = await requestMentor(`/cohorts/${id}`);
  return normalizeCohortDetail(unwrapData(data));
}

export function invalidateCohortsCache() {
  cohortsCache.clear();
}

export async function createCohort(payload) {
  const data = await requestMentor('/cohorts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  cohortsCache.clear();
  return normalizeCohortDetail(unwrapData(data));
}

export async function updateCohort(id, payload) {
  const data = await requestMentor(`/cohorts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  cohortsCache.clear();
  return normalizeCohortDetail(unwrapData(data));
}

export async function deleteCohort(id) {
  cohortsCache.clear();
  return requestMentor(`/cohorts/${id}`, { method: 'DELETE' });
}

export async function syncCohortStudents(id, studentIds) {
  const data = await requestMentor(`/cohorts/${id}/students`, {
    method: 'PUT',
    body: JSON.stringify({ student_ids: studentIds }),
  });
  return normalizeCohortDetail(unwrapData(data));
}

export async function addCohortStudents(id, studentIds) {
  const data = await requestMentor(`/cohorts/${id}/students`, {
    method: 'POST',
    body: JSON.stringify({ student_ids: studentIds }),
  });
  return unwrapData(data);
}

export async function removeCohortStudent(id, studentId) {
  return requestMentor(`/cohorts/${id}/students/${studentId}`, { method: 'DELETE' });
}

export async function getCohortRoleHistory(id) {
  const data = await requestMentor(`/cohorts/${id}/students/role-history`);
  return unwrapData(data) || [];
}

export async function getGroups(schoolId = null) {
  const token = getToken();
  const qs = schoolId ? `?school_id=${schoolId}` : '';
  const path = `/groups${qs}`;
  const key = cacheKey(MENTOR_BASE_URL, path, token);
  const cachedGroups = readCache(groupsCache, key, MENTOR_BASE_URL, path);
  if (cachedGroups != null) return cachedGroups;
  const promise = requestMentor(path).then(d => asArray(unwrapData(d)).map(normalizeGroup));
  groupsCache.set(key, promise);
  try {
    const result = await promise;
    groupsCache.set(key, result);
    return result;
  } catch (err) {
    groupsCache.delete(key);
    throw err;
  }
}

export async function getGroup(id) {
  const data = await requestMentor(`/groups/${id}`);
  return normalizeGroup(unwrapData(data));
}

export async function createGroup(payload) {
  const data = await requestMentor('/groups', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  groupsCache.clear();
  return normalizeGroup(unwrapData(data));
}

export async function updateGroup(id, payload) {
  const data = await requestMentor(`/groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  groupsCache.clear();
  return normalizeGroup(unwrapData(data));
}

export async function deleteGroup(id) {
  groupsCache.clear();
  return requestMentor(`/groups/${id}`, { method: 'DELETE' });
}

export async function getSessions(query = {}) {
  const token = getToken();
  const path = `/sessions${buildQuery(query)}`;
  const key = cacheKey(MENTOR_BASE_URL, path, token);
  const cachedSessions = readCache(sessionsCache, key, MENTOR_BASE_URL, '/sessions');
  if (cachedSessions != null) return cachedSessions;
  const promise = requestMentor(path).then(d => asArray(unwrapData(d)).map(normalizeSession));
  sessionsCache.set(key, promise);
  try {
    const result = await promise;
    sessionsCache.set(key, result);
    return result;
  } catch (err) {
    sessionsCache.delete(key);
    throw err;
  }
}

export async function getSession(id) {
  const data = await requestMentor(`/sessions/${id}`);
  return normalizeSession(unwrapData(data));
}

export async function createSession(payload) {
  const data = await requestMentor('/sessions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  sessionsCache.clear();
  return normalizeSession(unwrapData(data));
}

export async function updateSession(id, payload) {
  const data = await requestMentor(`/sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  sessionsCache.clear();
  return normalizeSession(unwrapData(data));
}

export async function deleteSession(id) {
  sessionsCache.clear();
  return requestMentor(`/sessions/${id}`, { method: 'DELETE' });
}

export async function assignSessionAuditor(sessionId, payload) {
  const data = await requestMentor(`/sessions/${sessionId}/auditors`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return unwrapData(data);
}

export async function removeSessionAuditor(sessionId, userId) {
  return requestMentor(`/sessions/${sessionId}/auditors/${userId}`, { method: 'DELETE' });
}

export async function respondToSessionAuditor(sessionId, status = 'accepted') {
  const data = await requestMentor(`/sessions/${sessionId}/auditors/respond`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  return unwrapData(data);
}

export async function withdrawSessionAuditor(sessionId) {
  return requestMentor(`/sessions/${sessionId}/auditors/withdraw`, { method: 'PATCH' });
}

export async function markSessionNoShow(sessionId, userId) {
  return requestMentor(`/sessions/${sessionId}/auditors/${userId}/no-show`, {
    method: 'PATCH',
    body: JSON.stringify({}),
  });
}

export async function getSessionNominations(sessionId, cohortId = null) {
  const path = cohortId
    ? `/sessions/${sessionId}/cohorts/${cohortId}/nominations`
    : `/sessions/${sessionId}/nominations`;
  const data = await requestMentor(path);
  return normalizeNomination(unwrapData(data));
}

export async function putSessionNominations(sessionId, payload, cohortId = null) {
  const path = cohortId
    ? `/sessions/${sessionId}/cohorts/${cohortId}/nominations`
    : `/sessions/${sessionId}/nominations`;
  const data = await requestMentor(path, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return normalizeNomination(unwrapData(data));
}

/** @deprecated Use putSessionNominations */
export async function upsertSessionNominations(sessionId, payload) {
  return putSessionNominations(sessionId, payload);
}

export async function getSessionOutcomes(sessionId, cohortId = null) {
  const path = cohortId
    ? `/sessions/${sessionId}/cohorts/${cohortId}/outcomes`
    : `/sessions/${sessionId}/outcomes`;
  const data = await requestMentor(path);
  return normalizeOutcome(unwrapData(data));
}

export async function putSessionOutcomes(sessionId, payload, cohortId = null) {
  const path = cohortId
    ? `/sessions/${sessionId}/cohorts/${cohortId}/outcomes`
    : `/sessions/${sessionId}/outcomes`;
  const data = await requestMentor(path, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return normalizeOutcome(unwrapData(data));
}

/** @deprecated Use putSessionOutcomes */
export async function upsertSessionOutcomes(sessionId, payload) {
  return putSessionOutcomes(sessionId, payload);
}

export async function getSessionAttendance(sessionId, cohortId) {
  const data = await requestMentor(`/sessions/${sessionId}/cohorts/${cohortId}/attendance`);
  return unwrapData(data);
}

export async function putSessionAttendance(sessionId, cohortId, absentStudentIds) {
  const data = await requestMentor(`/sessions/${sessionId}/cohorts/${cohortId}/attendance`, {
    method: 'PUT',
    body: JSON.stringify({ absent_student_ids: absentStudentIds }),
  });
  return unwrapData(data);
}

export async function getStudentAttendanceSummary(studentId) {
  const data = await requestMentor(`/students/${studentId}/attendance-summary`);
  return unwrapData(data);
}

export async function getAttendanceOverview() {
  const data = await requestMentor('/attendance/overview');
  return unwrapData(data) || [];
}

export async function getEvaluations(query = {}) {
  const token = getToken();
  const path = `/evaluations${buildQuery(query)}`;
  const key = cacheKey(MENTOR_BASE_URL, path, token);
  const cachedEvaluations = readCache(evaluationsCache, key, MENTOR_BASE_URL, '/evaluations');
  if (cachedEvaluations != null) return cachedEvaluations;
  const promise = requestMentor(path).then(d => asArray(unwrapData(d)).map(normalizeEvaluation));
  evaluationsCache.set(key, promise);
  try {
    const result = await promise;
    evaluationsCache.set(key, result);
    return result;
  } catch (err) {
    evaluationsCache.delete(key);
    throw err;
  }
}

export async function getStudentEvaluationLog(query = {}) {
  const data = await requestMentor(`/evaluations/log${buildQuery(query)}`);
  return asArray(unwrapData(data)).map(normalizeEvaluationLog);
}

export async function getEvaluation(id) {
  const data = await requestMentor(`/evaluations/${id}`);
  return normalizeEvaluation(unwrapData(data));
}

export async function createEvaluation(payload) {
  const data = await requestMentor('/evaluations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  evaluationsCache.clear();
  return normalizeEvaluation(unwrapData(data));
}

export async function updateEvaluation(id, payload) {
  const data = await requestMentor(`/evaluations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  evaluationsCache.clear();
  return normalizeEvaluation(unwrapData(data));
}

export async function submitEvaluation(id) {
  const data = await requestMentor(`/evaluations/${id}/submit`, { method: 'POST' });
  evaluationsCache.clear();
  return normalizeEvaluation(unwrapData(data));
}

export async function reopenEvaluation(id) {
  const data = await requestMentor(`/evaluations/${id}/reopen`, { method: 'POST' });
  evaluationsCache.clear();
  return normalizeEvaluation(unwrapData(data));
}

export async function approveEvaluation(id) {
  const data = await requestMentor(`/evaluations/${id}/approve`, { method: 'POST' });
  evaluationsCache.clear();
  return normalizeEvaluation(unwrapData(data));
}

export async function rejectEvaluation(id, rejectionReason) {
  const data = await requestMentor(`/evaluations/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejection_reason: rejectionReason }),
  });
  evaluationsCache.clear();
  return normalizeEvaluation(unwrapData(data));
}

export async function getPaDashboardStats() {
  const data = await requestMentor('/pa/dashboard/stats');
  return unwrapData(data);
}

export async function getYear2SquadSetupSchools() {
  const token = getToken();
  const key = cacheKey(MENTOR_BASE_URL, '/year2/squad-setup/schools', token);
  const cachedSchools = readCache(year2SetupSchoolsCache, key, MENTOR_BASE_URL, '/year2/squad-setup/schools');
  if (cachedSchools != null) return cachedSchools;
  const promise = requestMentor('/year2/squad-setup/schools').then(d => asArray(unwrapData(d)));
  year2SetupSchoolsCache.set(key, promise);
  try {
    const result = await promise;
    year2SetupSchoolsCache.set(key, result);
    return result;
  } catch (err) {
    year2SetupSchoolsCache.delete(key);
    throw err;
  }
}

export async function getYear2SquadSetupCohorts(schoolId) {
  const token = getToken();
  const path = `/year2/squad-setup/schools/${schoolId}/cohorts`;
  const key = cacheKey(MENTOR_BASE_URL, path, token);
  const cachedY2Cohorts = readCache(year2SetupCohortsBySchoolCache, key, MENTOR_BASE_URL, path);
  if (cachedY2Cohorts != null) return cachedY2Cohorts;
  const promise = requestMentor(path).then(d => asArray(unwrapData(d)));
  year2SetupCohortsBySchoolCache.set(key, promise);
  try {
    const result = await promise;
    year2SetupCohortsBySchoolCache.set(key, result);
    return result;
  } catch (err) {
    year2SetupCohortsBySchoolCache.delete(key);
    throw err;
  }
}

/** All Year 2 cohorts scoped to the authenticated SPOC (same source as Squad Creator). */
export async function getYear2SquadSetupCohortsForUser() {
  const token = getToken();
  const key = cacheKey(MENTOR_BASE_URL, '/year2/squad-setup/cohorts-for-user', token);
  const cachedY2UserCohorts = readCache(year2SetupCohortsCache, key, MENTOR_BASE_URL, '/year2/squad-setup/cohorts-for-user');
  if (cachedY2UserCohorts != null) return cachedY2UserCohorts;

  const promise = (async () => {
    const schools = await getYear2SquadSetupSchools();
    const cohorts = [];
    for (const school of schools) {
      const rows = await getYear2SquadSetupCohorts(school.school_id);
      cohorts.push(...rows);
    }
    return cohorts;
  })();

  year2SetupCohortsCache.set(key, promise);
  try {
    const cohorts = await promise;
    year2SetupCohortsCache.set(key, cohorts);
    return cohorts;
  } catch (err) {
    year2SetupCohortsCache.delete(key);
    throw err;
  }
}

export async function getYear2SquadSetup(cohortId) {
  const token = getToken();
  const key = cacheKey(MENTOR_BASE_URL, `/year2/squad-setup/cohorts/${cohortId}`, token);
  const cachedY2Setup = readCache(year2SetupCache, key, MENTOR_BASE_URL, `/year2/squad-setup/cohorts/${cohortId}`);
  if (cachedY2Setup != null) return cachedY2Setup;

  const promise = requestMentor(`/year2/squad-setup/cohorts/${cohortId}`)
    .then(unwrapData);

  year2SetupCache.set(key, promise);
  try {
    const data = await promise;
    year2SetupCache.set(key, data);
    return data;
  } catch (err) {
    year2SetupCache.delete(key);
    throw err;
  }
}

export async function saveYear2SquadSetup(cohortId, squads, options = {}) {
  const data = await requestMentor(`/year2/squad-setup/cohorts/${cohortId}`, {
    method: 'PUT',
    body: JSON.stringify({
      squads: squads.map((squad) => ({
        name: squad.name,
        topic: squad.topic,
        member_ids: (squad.member_ids || []).map((id) => Number(id)),
      })),
      allow_unassigned: Boolean(options.allowUnassigned),
    }),
  });
  year2SetupCache.delete(cacheKey(MENTOR_BASE_URL, `/year2/squad-setup/cohorts/${cohortId}`, getToken()));
  year2SetupCohortsCache.clear();
  year2SetupSchoolsCache.clear();
  year2SetupCohortsBySchoolCache.clear();
  return unwrapData(data);
}

export async function finalizeYear2SquadSetup(cohortId) {
  const data = await requestMentor(`/year2/squad-setup/cohorts/${cohortId}/finalize`, {
    method: 'POST',
  });
  year2SetupCache.delete(cacheKey(MENTOR_BASE_URL, `/year2/squad-setup/cohorts/${cohortId}`, getToken()));
  year2SetupCohortsCache.clear();
  year2SetupSchoolsCache.clear();
  year2SetupCohortsBySchoolCache.clear();
  return unwrapData(data);
}

export async function unlockYear2SquadSetup(cohortId) {
  const data = await requestMentor(`/year2/squad-setup/cohorts/${cohortId}/unlock`, {
    method: 'POST',
  });
  year2SetupCache.delete(cacheKey(MENTOR_BASE_URL, `/year2/squad-setup/cohorts/${cohortId}`, getToken()));
  year2SetupCohortsCache.clear();
  year2SetupSchoolsCache.clear();
  year2SetupCohortsBySchoolCache.clear();
  return unwrapData(data);
}

export async function getYear2Squads(cohortId) {
  const data = await requestMentor(`/year2/squads${buildQuery({ cohort_id: cohortId })}`);
  return asArray(unwrapData(data));
}

export async function createYear2Squad(cohortId, name, extra = {}) {
  const data = await requestMentor('/year2/squads', {
    method: 'POST',
    body: JSON.stringify({ cohort_id: cohortId, name, ...extra }),
  });
  return unwrapData(data);
}

export async function updateYear2Squad(squadId, payload) {
  const data = await requestMentor(`/year2/squads/${squadId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return unwrapData(data);
}

export async function deleteYear2Squad(squadId) {
  const data = await requestMentor(`/year2/squads/${squadId}`, { method: 'DELETE' });
  return unwrapData(data);
}

export async function syncYear2SquadMembers(squadId, studentIds) {
  const data = await requestMentor(`/year2/squads/${squadId}/members`, {
    method: 'PUT',
    body: JSON.stringify({ student_ids: studentIds }),
  });
  return unwrapData(data);
}

export async function upsertYear2ChairRotation(squadId, councilNumber, chairStudentId, deputyStudentId = null) {
  const data = await requestMentor(`/year2/squads/${squadId}/chair-rotations`, {
    method: 'PUT',
    body: JSON.stringify({
      council_number: councilNumber,
      chair_student_id: chairStudentId,
      deputy_student_id: deputyStudentId,
    }),
  });
  return unwrapData(data);
}

export async function getYear2ChairRotations(squadId) {
  const data = await requestMentor(`/year2/squads/${squadId}/chair-rotations`);
  return asArray(unwrapData(data));
}

export async function getYear2CouncilNominations(cohortId, councilNumber) {
  const token = getToken();
  const path = `/year2/cohorts/${cohortId}/council-nominations${buildQuery({
    council_number: councilNumber,
  })}`;
  const key = cacheKey(MENTOR_BASE_URL, path, token);
  const cachedCouncil = readCache(year2CouncilNominationsCache, key, MENTOR_BASE_URL, path);
  if (cachedCouncil != null) return cachedCouncil;

  const promise = requestMentor(path).then(unwrapData);
  year2CouncilNominationsCache.set(key, promise);
  try {
    const data = await promise;
    year2CouncilNominationsCache.set(key, data);
    return data;
  } catch (err) {
    year2CouncilNominationsCache.delete(key);
    throw err;
  }
}

export async function saveYear2CouncilNominations(cohortId, councilNumber, squads) {
  const token = getToken();
  const readPath = `/year2/cohorts/${cohortId}/council-nominations${buildQuery({
    council_number: councilNumber,
  })}`;
  const data = await requestMentor(`/year2/cohorts/${cohortId}/council-nominations`, {
    method: 'PUT',
    body: JSON.stringify({
      council_number: Number(councilNumber),
      squads: squads.map((row) => ({
        squad_id: Number(row.squad_id),
        chair_student_id: Number(row.chair_student_id),
        deputy_student_id: row.deputy_student_id != null && row.deputy_student_id !== ''
          ? Number(row.deputy_student_id)
          : null,
      })),
    }),
  });
  const payload = unwrapData(data);
  year2CouncilNominationsCache.set(cacheKey(MENTOR_BASE_URL, readPath, token), payload);
  return payload;
}

export async function getYear2SessionAudit(sessionId, auditorId = null) {
  const data = await requestMentor(`/year2/session-audits${buildQuery({
    session_id: sessionId,
    auditor_id: auditorId,
  })}`);
  return asArray(unwrapData(data));
}

export async function upsertYear2SessionAudit(payload) {
  const data = await requestMentor('/year2/session-audits', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return unwrapData(data);
}

export async function submitYear2SessionAudit(auditId) {
  const data = await requestMentor(`/year2/session-audits/${auditId}/submit`, { method: 'PUT' });
  return unwrapData(data);
}

export async function reopenYear2SessionAudit(auditId) {
  const data = await requestMentor(`/year2/session-audits/${auditId}/reopen`, { method: 'POST' });
  return unwrapData(data);
}

export async function getYear2EvaluationWorkspace(sessionId) {
  const data = await requestMentor(`/year2/sessions/${sessionId}/evaluation-workspace`);
  return unwrapData(data);
}

export async function getYear2StudentAssessments(sessionId, squadId = null) {
  const data = await requestMentor(`/year2/student-assessments${buildQuery({
    session_id: sessionId,
    squad_id: squadId,
  })}`);
  return asArray(unwrapData(data));
}

export async function upsertYear2StudentAssessment(payload) {
  const data = await requestMentor('/year2/student-assessments', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return unwrapData(data);
}

export async function submitYear2StudentAssessment(id) {
  const data = await requestMentor(`/year2/student-assessments/${id}/submit`, { method: 'PUT' });
  return unwrapData(data);
}

export async function reopenYear2StudentAssessment(id) {
  const data = await requestMentor(`/year2/student-assessments/${id}/reopen`, { method: 'POST' });
  return unwrapData(data);
}

export async function approveYear2StudentAssessment(id) {
  const data = await requestMentor(`/year2/student-assessments/${id}/approve`, { method: 'PUT' });
  return unwrapData(data);
}

export async function rejectYear2StudentAssessment(id, rejectionReason) {
  const data = await requestMentor(`/year2/student-assessments/${id}/reject`, {
    method: 'PUT',
    body: JSON.stringify({ rejection_reason: rejectionReason }),
  });
  return unwrapData(data);
}

export async function upsertYear2SquadAssessment(payload) {
  const data = await requestMentor('/year2/squad-assessments', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return unwrapData(data);
}

export async function submitYear2SquadAssessment(id) {
  const data = await requestMentor(`/year2/squad-assessments/${id}/submit`, { method: 'PUT' });
  return unwrapData(data);
}

export async function reopenYear2SquadAssessment(id) {
  const data = await requestMentor(`/year2/squad-assessments/${id}/reopen`, { method: 'POST' });
  return unwrapData(data);
}

export async function approveYear2SquadAssessment(id) {
  const data = await requestMentor(`/year2/squad-assessments/${id}/approve`, { method: 'PUT' });
  return unwrapData(data);
}

export async function rejectYear2SquadAssessment(id, rejectionReason) {
  const data = await requestMentor(`/year2/squad-assessments/${id}/reject`, {
    method: 'PUT',
    body: JSON.stringify({ rejection_reason: rejectionReason }),
  });
  return unwrapData(data);
}

export async function getYear2EvaluationsQueue({ status = 'submitted', session_id, cohort_id } = {}) {
  const data = await requestMentor(`/year2/evaluations${buildQuery({
    status,
    session_id,
    cohort_id,
  })}`);
  return asArray(unwrapData(data));
}

export async function getYear2StudentEvaluationLog({ student_id, session_id, cohort_id } = {}) {
  const data = await requestMentor(`/year2/evaluations/log${buildQuery({
    student_id,
    session_id,
    cohort_id,
  })}`);
  return asArray(unwrapData(data));
}

export async function getYear2SquadOutputs(squadId) {
  const data = await requestMentor(`/year2/squads/${squadId}/outputs`);
  return asArray(unwrapData(data));
}

export async function upsertYear2SquadOutput(squadId, outputType, payload = {}) {
  const data = await requestMentor(`/year2/squads/${squadId}/outputs`, {
    method: 'PUT',
    body: JSON.stringify({ output_type: outputType, ...payload }),
  });
  return unwrapData(data);
}

export async function reviewYear2Output(outputId, status, comment = null) {
  const data = await requestMentor(`/year2/outputs/${outputId}/review`, {
    method: 'PUT',
    body: JSON.stringify({ status, comment }),
  });
  return unwrapData(data);
}

export async function getYear2PortfolioStatus(squadId) {
  const data = await requestMentor(`/year2/squads/${squadId}/portfolio-status`);
  return unwrapData(data);
}

export async function sealYear2Portfolio(squadId) {
  const data = await requestMentor(`/year2/squads/${squadId}/seal`, { method: 'POST' });
  return unwrapData(data);
}

// ── ESG Specialist — squad formation ───────────────────────────────────────
export async function getEsgLevels() {
  const data = await requestMentor('/esg/levels');
  return asArray(unwrapData(data));
}

export async function getEsgAvailableStudents(levelId) {
  const data = await requestMentor(`/esg/levels/${levelId}/available-students`);
  return asArray(unwrapData(data));
}

// Paginated for the ESG Squads screen's infinite scroll - `meta.has_more`
// says whether another page exists. Filters/search/sort are all server-side.
export async function getEsgSquads({ levelId, status, search, sort, page = 1, perPage = 20 } = {}) {
  const data = await requestMentor(`/esg/squads${buildQuery({
    level_id: levelId,
    status,
    search: search?.trim(),
    sort,
    page,
    per_page: perPage,
  })}`);
  return {
    items: asArray(unwrapData(data)),
    meta: data?.meta || { page, per_page: perPage, total: 0, has_more: false },
  };
}

export async function createEsgSquad(payload) {
  const data = await requestMentor('/esg/squads', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return unwrapData(data);
}

// ── ESG Specialist — Support inbox (T7) ─────────────────────────────────────
// Council-related messages from students: a squad's assigned auditors and the PA read and answer them.
// status: 'open' | 'answered' | 'all'.
export async function getEsgSupportMessages(status = 'open') {
  const data = await requestMentor(`/esg/support-messages${buildQuery({ status })}`);
  return asArray(unwrapData(data));
}

export async function replyToEsgSupportMessage(id, reply) {
  const data = await requestMentor(`/esg/support-messages/${id}/reply`, { method: 'POST', body: JSON.stringify({ reply }) });
  return unwrapData(data);
}

// ── ESG Specialist — auditor evaluations ───────────────────────────────────
// PA (any squad), a squad's own SPOC and its assigned auditors may evaluate; only a PA approves/rejects.
export async function getEsgEvaluationSquads() {
  const data = await requestMentor('/esg/evaluation/squads');
  return asArray(unwrapData(data));
}

export async function getEsgEvaluationWorkspace(squadId, council) {
  const data = await requestMentor(`/esg/squads/${squadId}/councils/${council}/evaluation`);
  return unwrapData(data);
}

// The evaluation is three independent steps, each confirmed once (a program administrator can reopen one).
export async function submitEsgSquadEvaluation(squadId, council, payload) {
  const data = await requestMentor(`/esg/squads/${squadId}/councils/${council}/evaluation/squad`, { method: 'POST', body: JSON.stringify(payload) });
  return unwrapData(data);
}

export async function submitEsgChairEvaluation(squadId, council, payload) {
  const data = await requestMentor(`/esg/squads/${squadId}/councils/${council}/evaluation/chair`, { method: 'POST', body: JSON.stringify(payload) });
  return unwrapData(data);
}

// One member's individual task: {tasks: [...]} for what they submitted, or {outcome: 'not_submitted'} (scores 0).
export async function submitEsgTaskEvaluation(squadId, council, studentId, payload) {
  const data = await requestMentor(`/esg/squads/${squadId}/councils/${council}/evaluation/tasks/${studentId}`, { method: 'POST', body: JSON.stringify(payload) });
  return unwrapData(data);
}

// part = 'squad' | 'chair' | 'task' (with student_id). Program administrators only.
export async function reopenEsgEvaluationStep(squadId, council, payload) {
  const data = await requestMentor(`/esg/squads/${squadId}/councils/${council}/evaluation/reopen`, { method: 'POST', body: JSON.stringify(payload) });
  return unwrapData(data);
}

export async function getEsgSquadRatings(squadId) {
  const data = await requestMentor(`/esg/squads/${squadId}/ratings`);
  return unwrapData(data);
}

// ESG certificates: students LMS found eligible (all three score components done, weighted score at least 40).
// A PA sees every student; a SPOC or auditor only students of their own squads. Approving issues the certificate.
export async function getEsgCertificates({ status, level } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (level) params.set('level', String(level));
  const query = params.toString();
  const data = await requestMentor(`/esg/certificates${query ? `?${query}` : ''}`);
  return asArray(unwrapData(data));
}

// One certificate with LMS's full report snapshot (every council's details) for the approver to read.
export async function getEsgCertificate(id) {
  const data = await requestMentor(`/esg/certificates/${id}`);
  return unwrapData(data);
}

export async function approveEsgCertificate(id) {
  const data = await requestMentor(`/esg/certificates/${id}/approve`, { method: 'POST' });
  return unwrapData(data);
}

// Read-only Introduction council (council 0) of a squad: date, minutes, attendance, profile, chairs, ratings.
export async function getEsgIntroduction(squadId) {
  const data = await requestMentor(`/esg/squads/${squadId}/introduction`);
  return unwrapData(data);
}

// Staff change of a mission's chair (allowed until one of the mission's councils has started). A 409 means the
// student is already chair of another mission and the change needs confirming (confirmDuplicate = true).
export async function updateEsgChair(squadId, mission, studentId, confirmDuplicate = false) {
  const data = await requestMentor(`/esg/squads/${squadId}/chair-rotations/${mission}`, {
    method: 'PUT',
    body: JSON.stringify({ student_id: studentId, confirm_duplicate: confirmDuplicate }),
  });
  return unwrapData(data);
}

// Change a squad's Introduction session lead (allowed until Council 1 has started).
export async function changeEsgAdminLead(squadId, studentId) {
  const data = await requestMentor(`/esg/squads/${squadId}/admin-lead`, {
    method: 'PUT',
    body: JSON.stringify({ student_id: studentId }),
  });
  return unwrapData(data);
}

// Mark a squad member as dropped out (reason required). Under 3 active members the squad is paused and the directorate emailed.
export async function dropOutEsgMember(squadId, studentId, reason) {
  const data = await requestMentor(`/esg/squads/${squadId}/members/${studentId}/drop-out`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return unwrapData(data);
}

// Let a paused squad continue (program administrator or an assigned auditor).
export async function resumeEsgSquad(squadId) {
  const data = await requestMentor(`/esg/squads/${squadId}/resume`, { method: 'POST' });
  return unwrapData(data);
}

// Strengths / coaching-area options the ESG evaluation form offers (program administrators only).
export async function getEsgTags() {
  const data = await requestMentor('/esg/tags');
  return asArray(unwrapData(data));
}

export async function createEsgTag({ audience, kind, label, description }) {
  const data = await requestMentor('/esg/tags', {
    method: 'POST',
    body: JSON.stringify({ audience, kind, label, description: description || null }),
  });
  return unwrapData(data);
}

// Only is_active, sort_order and description can change; a tag's label is never edited.
export async function updateEsgTag(id, changes) {
  const data = await requestMentor(`/esg/tags/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
  return unwrapData(data);
}

// Generic, program-agnostic - already-synced local people by role (see
// mentor-api's UserController). Not Central's /teachers or /auditors -
// those return Central's own ids, a different id space than spoc_id/
// auditor_assignments.*.user_id expect (mentor-api's local users.id).
export async function getUsersByRole(role) {
  const data = await requestMentor(`/users?role=${role}`);
  return asArray(unwrapData(data));
}

// Reports & Insights — role-scoped performance dashboard (Counselor + Auditor only, never SPOC).
export async function getReportsOverview(role, period = 'quarter') {
  const data = await requestMentor(`/reports/overview?role=${role}&period=${period}`);
  return unwrapData(data);
}

export async function getReportsLeaderboard(role, period = 'quarter') {
  const data = await requestMentor(`/reports/leaderboard?role=${role}&period=${period}`);
  return unwrapData(data);
}

export async function getReportsProfile(role, id, period = 'quarter') {
  const data = await requestMentor(`/reports/profile/${id}?role=${role}&period=${period}`);
  return unwrapData(data);
}

export async function getReportsSelf(period = 'quarter') {
  const data = await requestMentor(`/reports/self?period=${period}`);
  return unwrapData(data);
}

export async function getActionItems() {
  const data = await requestMentor('/action-items');
  return unwrapData(data);
}

export async function getMyProfile() {
  const data = await requestMentor('/me/profile');
  return unwrapData(data);
}

export async function saveMyProfile(payload) {
  const data = await requestMentor('/me/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return unwrapData(data);
}

// Normalize live API resource shape -> internal app shape.
export function normalizeResources(items) {
  return (items || []).map(r => {
    const rawLevel = String(r.level_name ?? r.levelName ?? '').trim();
    const normalizedLevel = API_LEVEL_TO_INTERNAL[rawLevel]
      || API_LEVEL_TO_INTERNAL[Object.keys(API_LEVEL_TO_INTERNAL).find(key => key.toLowerCase() === rawLevel.toLowerCase())]
      || rawLevel;
    const activeStatus = r.active_status_id ?? r.activeStatusId ?? r.status;
    return {
      id: String(r.id),
      refId: r.resource_master_id ?? r.resourceMasterId,
      title: r.title || '',
      description: r.description || '',
      introduction: r.introduction || '',
      fileType: r.file_type || r.fileType || 'pdf',
      displayOrder: parseFloat(r.display_order ?? r.displayOrder) || 0,
      councilId: Number(r.session_id ?? r.sessionId ?? r.council_id ?? r.councilId),
      levelName: normalizedLevel,
      programName: String(r.program_name ?? r.programName ?? 'Glosco').trim(),
      status: Number(activeStatus) === 1 || String(activeStatus).toLowerCase() === 'active' ? 'active' : 'inactive',
      viewUrl: r.view_url || r.viewUrl || '',
      downloadUrl: r.download_url || r.downloadUrl || '',
      thumbnailUrl: r.thumbnail_url || r.thumbnailUrl || '',
    };
  });
}

export async function getPaApiActivityLogs(params = {}) {
  const data = await requestMentor(`/pa/api-activity-logs${buildQuery({
    host: params.host,
    path: params.path,
    user_role: params.user_role,
    from: params.from,
    to: params.to,
    errors_only: params.errors_only,
    limit: params.limit || 200,
  })}`, { callerTag: 'log:read' });
  return asArray(unwrapData(data));
}

mentorRequestLog.setFlushHandler(async (entries) => {
  await requestMentor('/pa/api-activity-logs/batch', {
    method: 'POST',
    body: JSON.stringify({ entries }),
    callerTag: 'log:flush',
  });
});
